import Captcha from 'Components/ContractGenerator/layouts/Captcha';
import Card from 'Components/ContractGenerator/layouts/Card';
import Section from 'Components/ContractGenerator/layouts/Section';
import Constants from 'Components/ContractGenerator/shared/constants';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { contractGeneratorOperations, contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import selectorStateComparison from 'Redux/utilities/selectorStateComparison';
import Alert from 'ViatoUi/Extranet/Surfaces/Alert';
import UserActionFeedback from 'ViatoUi/Shared/Feedback/UserActionFeedback';
import Spacer from 'ViatoUi/Shared/Layouts/Spacer';
import Button from 'ViatoUi/Web/FormElements/Buttons/Button';
import Input from 'ViatoUi/Web/FormElements/Inputs/Input';

const Login = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation('contractGenerator');

	const captchaResponse = useSelector(contractGeneratorSelectors.getCaptchaResponse);
	const user = useSelector(contractGeneratorSelectors.getUser);

	const [userCredentials, setUserCredentials] = useState({ username: '', password: '' });
	const [rerenderCaptcha, setRerenderCaptcha] = useState(0);

	const [submitCallPending, submitCallError, submitCallErrorCode, submitCallErrorMessage] = useSelector(
		contractGeneratorSelectors.getLoginApiCall(),
		(formerState, currentState) => selectorStateComparison(formerState, currentState)
	);

	const errorMessage = useMemo(() => {
		switch (submitCallErrorCode) {
			case 516:
				return t('pages.login.errorMessages.516');
			case 517:
				return t('pages.login.errorMessages.517');
			case 518:
				return t('pages.login.errorMessages.518');
			case 519:
				return t('pages.login.errorMessages.519');
			default:
				return t('pages.login.errorMessages.unknown');
		}
	}, [submitCallErrorCode]);

	return (
		<>
			<Section headline={t('pages.login.login')} firstElementOfStep>
				{user.isLoggedIn ? (
					<>
						<Card headline={t('pages.login.success')} isSmall>
							<Alert display='block' type='success'>
								{t('pages.login.successInfo')}
							</Alert>
							<Spacer height='xl' />
							<Button
								type='primary'
								onClick={() => dispatch(contractGeneratorOperations.setActiveStep(Constants.STEPS.STEP1))}
							>
								{t('pages.login.back')}
							</Button>
						</Card>
					</>
				) : (
					<>
						<Input
							placeholder={t('pages.login.username')}
							value={userCredentials.username}
							onChange={newUsername => setUserCredentials({ ...userCredentials, username: newUsername })}
						/>
						<Spacer height='m' />
						<Input
							placeholder={t('pages.login.password')}
							type='password'
							value={userCredentials.password}
							onChange={newPassword => setUserCredentials({ ...userCredentials, password: newPassword })}
						/>

						<Spacer height='l' />
						<Captcha forceReset={rerenderCaptcha} />
						<Spacer height='xl' />

						<Button
							disabled={captchaResponse === ''}
							tooltip={captchaResponse === '' ? t('app.captchaRequired') : null}
							type='secondary'
							onClick={() => dispatch(contractGeneratorOperations.login({ ...userCredentials, captchaResponse }))}
						>
							{t('pages.login.login')}
						</Button>
						<Spacer height='s' />
						<Button type='ghost' onClick={() => dispatch(contractGeneratorOperations.setActiveStep(Constants.STEPS.STEP1))}>
							{t('app.userActions.cancel')}
						</Button>
					</>
				)}
			</Section>
			<UserActionFeedback
				error={!!submitCallError}
				errorCode={submitCallErrorCode}
				errorMessage={submitCallErrorMessage}
				pending={!!submitCallPending}
				type='modal'
				userMessage={errorMessage}
				isSilent
				showContractLoader
				onError={() => setRerenderCaptcha(Date.now())}
			/>
		</>
	);
};

export default Login;
